import React, { useState, useEffect, useMemo } from "react";
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

const CARD_WIDTH = 60;
const CARD_HEIGHT = Math.round((CARD_WIDTH * 10) / 7);
const TABLEAU_STACK_OFFSET = 16;

// Helper: can this card be moved to a tableau slot?
function canMoveToTableau(card, tableauSlot) {
  if (!card || !Array.isArray(tableauSlot)) return false;
  if (tableauSlot.length === 0) {
    // Game rule override: allow any card to be moved to an empty tableau slot
    return true;
  }
  const top = tableauSlot[tableauSlot.length - 1];
  // Must be alternating color and descending rank
  const redSuits = ["hearts", "diamonds"];
  const order = [
    "A",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "J",
    "Q",
    "K",
  ];
  const cardColor = redSuits.includes(card.suit) ? "red" : "black";
  const topColor = redSuits.includes(top.suit) ? "red" : "black";
  // Card can be placed if it is opposite color and one rank lower than the top card
  return (
    cardColor !== topColor &&
    order.indexOf(card.rank) === order.indexOf(top.rank) - 1
  );
}

// Helper to render a card image
function CardImg({ card, style }) {
  if (!card) return null;
  // If card is an object, use card.code or card.filename; if string, use directly
  let filename = typeof card === "string" ? card : card.code || card.filename;
  if (!filename) return null;
  // Example: "2_of_clubs.png" or "card_back.png"
  return (
    <img
      src={`/cards/${filename}`}
      alt={filename.replace(".png", "")}
  style={{ width: CARD_WIDTH, height: CARD_HEIGHT, marginRight: 6, borderRadius: 8, boxShadow: '0 8px 16px rgba(15,23,42,0.14)', objectFit: 'cover', display: 'block', ...style }}
      draggable={false}
    />
  );
}

function CardButton({
  card,
  hidden = false,
  onClick,
  onDragStart,
  onDragEnd,
  draggable = false,
  onTouchDragStart,
  onTouchDragMove,
  onTouchDragEnd,
  disabled = false,
  selected = false,
  highlighted = false,
  borderless = false,
  style = {},
  title,
}) {
  const touchDragState = React.useRef({ dragging: false, started: false, suppressClick: false, startX: 0, startY: 0 });

  function handleTouchStart(event) {
    if (disabled) return;
    const touch = event.touches && event.touches[0];
    touchDragState.current.dragging = true;
    touchDragState.current.started = false;
    if (touch) {
      touchDragState.current.startX = touch.clientX;
      touchDragState.current.startY = touch.clientY;
    }
  }

  function handleTouchMove(event) {
    if (disabled || !touchDragState.current.dragging) return;
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    const dx = touch.clientX - touchDragState.current.startX;
    const dy = touch.clientY - touchDragState.current.startY;
    if (!touchDragState.current.started && (dx * dx) + (dy * dy) > 64) {
      touchDragState.current.started = true;
      if (onTouchDragStart) onTouchDragStart(event);
    }
    if (touchDragState.current.started && onTouchDragMove) {
      event.preventDefault();
      onTouchDragMove(event);
    }
  }

  function resetTouchState() {
    touchDragState.current.dragging = false;
    touchDragState.current.started = false;
  }

  function handleTouchEndOrCancel(event) {
    if (touchDragState.current.started && onTouchDragEnd) {
      touchDragState.current.suppressClick = true;
      onTouchDragEnd(event);
    }
    resetTouchState();
  }

  function handleClick(event) {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (touchDragState.current.suppressClick) {
      touchDragState.current.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (onClick) onClick(event);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      draggable={draggable}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEndOrCancel}
      onTouchCancel={handleTouchEndOrCancel}
      disabled={disabled}
      title={title}
      style={{
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        padding: 0,
        background: 'transparent',
        borderRadius: 8,
        border: borderless
          ? 'none'
          : selected
            ? '2px solid rgba(251,191,36,0.95)'
            : highlighted
              ? '2px solid rgba(56,189,248,0.95)'
              : '1px solid rgba(148,163,184,0.35)',
        outline: borderless
          ? selected
            ? '2px solid rgba(251,191,36,0.95)'
            : highlighted
              ? '2px solid rgba(56,189,248,0.95)'
              : 'none'
          : 'none',
        outlineOffset: borderless ? -2 : 0,
        boxShadow: selected
          ? '0 0 0 3px rgba(253,224,71,0.32), 0 12px 20px rgba(15,23,42,0.14)'
          : highlighted
            ? '0 0 0 3px rgba(125,211,252,0.24), 0 12px 20px rgba(15,23,42,0.14)'
            : '0 10px 18px rgba(15,23,42,0.14)',
        overflow: 'hidden',
        cursor: disabled ? 'default' : draggable || onTouchDragStart ? 'grab' : 'pointer',
        touchAction: !disabled && (draggable || onTouchDragStart) ? 'none' : 'manipulation',
        ...style,
      }}
    >
      {hidden ? (
        <img src={'/cards/card_back.png'} alt="Card back" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
      ) : (
        <img src={`/cards/${typeof card === 'string' ? card : card.code || card.filename}`} alt={typeof card === 'string' ? card : `${card.rank} of ${card.suit}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} draggable={false} />
      )}
    </button>
  );
}

function EmptySlot({ highlighted = false, foundation = false, darkMode = false, content = null, onClick, onDragOver, onDrop, dataDropTarget, title, style = {} }) {
  const baseBorder = foundation
    ? (highlighted ? '2px dashed rgba(56,189,248,0.9)' : '2px dashed rgba(148,163,184,0.4)')
    : darkMode
      ? (highlighted ? '2px dashed rgba(56,189,248,0.9)' : '2px dashed rgba(100,116,139,0.8)')
      : (highlighted ? '2px solid rgba(56,189,248,0.9)' : '2px dashed rgba(148,163,184,0.45)');
  const baseBackground = foundation
    ? 'transparent'
    : darkMode
      ? 'transparent'
      : (highlighted ? 'rgba(186,230,253,0.28)' : 'rgba(255,255,255,0.38)');
  const baseBoxShadow = foundation
    ? (highlighted
        ? '0 0 0 3px rgba(125,211,252,0.18)'
        : 'inset 0 0px 0 rgba(255,255,255,0.5), 0 1px 2px rgba(15,23,42,0.06)')
    : darkMode
      ? 'none'
      : (highlighted ? '0 0 0 3px rgba(125,211,252,0.18)' : 'inset 0 1px 0 rgba(255,255,255,0.4)');
  return (
    <div
      onClick={onClick}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-drop-target={dataDropTarget}
      title={title}
      style={{
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        border: baseBorder,
        background: baseBackground,
        boxShadow: baseBoxShadow,
        cursor: 'pointer',
        ...style,
      }}
    >
      {content ? content : !foundation ? (
        <div style={{ color: highlighted ? 'rgba(3,105,161,0.85)' : 'rgba(71,85,105,0.34)', fontSize: 18, fontWeight: 700 }}>+</div>
      ) : null}
    </div>
  );
}

function NertzGame({ gameState, gamePlayers = [], user = {}, socket, roomId, isSpectator = false }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark') || document.body.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined;

    const updateDarkMode = () => {
      setIsDarkMode(
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark')
      );
    };

    updateDarkMode();

    const observer = new MutationObserver(updateDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // --- Spectator Perspective logic (modular, opt-in) ---
  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);
  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer: useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]) });
  const matchesPerspectivePlayer = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);
  const players = useMemo(() => (Array.isArray(gameState?.players) ? gameState.players : []), [gameState?.players]);
  const currentPlayer = useMemo(() => {
    return players.find(matchesPerspectivePlayer) || null;
  }, [players, matchesPerspectivePlayer]);
  const sortedPlayers = currentPlayer
    ? [
        currentPlayer,
        ...players.filter((p) => String(p.socketId) !== String(currentPlayer.socketId)),
      ]
    : players;
  // Show selector if in spectator mode and options exist
  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  // Ensure a default player is always selected for spectating
  useEffect(() => {
    if (isSpectator && perspectiveOptions.length > 0 && !spectatorViewPlayerId) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [isSpectator, perspectiveOptions, spectatorViewPlayerId]);
  // UI state: which card (if any) currently has its tiny action menu open
  const [actionMenu, setActionMenu] = useState(null);
  const foundationRefs = React.useRef([]);
  const tableauRefs = React.useRef({});
  const pileRefs = React.useRef({});
  const [overlayTargets, setOverlayTargets] = useState([]);
  const animatedCards = [];
  // Track in-progress animations so we can hide source/target piles while animating
  const liveAnimations = [];
  const [selectedSource, setSelectedSource] = useState(null);
  const [highlightedTargets, setHighlightedTargets] = useState([]);
  const [touchDragPreview, setTouchDragPreview] = useState(null);
  const dragTouch = React.useRef(null);
  const desktopDragPreviewRef = React.useRef(null);

  function clearDesktopDragPreview() {
    if (desktopDragPreviewRef.current && desktopDragPreviewRef.current.parentNode) {
      desktopDragPreviewRef.current.parentNode.removeChild(desktopDragPreviewRef.current);
    }
    desktopDragPreviewRef.current = null;
  }

  function clearSelectionState() {
    setSelectedSource(null);
    setHighlightedTargets([]);
    dragTouch.current = null;
    setTouchDragPreview(null);
    clearDesktopDragPreview();
  }

  function isHighlightedTarget(targetKey) {
    return highlightedTargets.includes(targetKey);
  }

  useEffect(() => {
    setSelectedSource(null);
    setHighlightedTargets([]);
    dragTouch.current = null;
    setTouchDragPreview(null);
    clearDesktopDragPreview();
  }, [gameState?.phase, gameState?.finished]);

  // Helper to get a filename for a card object or filename string
  function getCardFilename(c) {
    if (!c) return null;
    if (typeof c === 'string') return c;
    if (c.filename) return c.filename;
    if (c.code) return c.code;
    const rank = String(c.rank || c.value || '').toLowerCase();
    const suit = String(c.suit || '').toLowerCase();
    if (!rank || !suit) return null;
    const map = { a: 'a', j: 'j', q: 'q', k: 'k' };
    const r = map[rank] || rank;
    return `${r}_of_${suit}.png`;
  }
  useEffect(() => {
    if (!actionMenu || !actionMenu.options || actionMenu.options.length === 0) {
      setOverlayTargets([]);
      return;
    }
    // compute DOM rects for each option
    const targets = [];
    // find the player state to determine which card(s) are being moved
    const playerState = gameState.players.find((pl) => String(pl.socketId) === String(actionMenu.playerId));
    function cardFilenameFromObj(c) {
      if (!c) return null;
      if (typeof c === 'string') return c;
      if (c.filename) return c.filename;
      if (c.code) return c.code;
      // build filename from rank/suit if possible
      const rank = String(c.rank || c.value || '').toLowerCase();
      const suit = String(c.suit || '').toLowerCase();
      if (!rank || !suit) return null;
      const map = { a: 'a', j: 'j', q: 'q', k: 'k' };
      const r = map[rank] || rank;
      return `${r}_of_${suit}.png`;
    }

    actionMenu.options.forEach((opt) => {
      let el = null;
      if (opt.type === 'foundation') {
        el = foundationRefs.current[opt.index];
      } else if (opt.type === 'tableau') {
        el = tableauRefs.current[`${actionMenu.playerId}:${opt.index}`];
      }
      if (!el || !el.getBoundingClientRect) return;
      // determine the card being moved from actionMenu.from and playerState
      let movingCard = null;
      if (playerState) {
        const from = actionMenu.from || {};
        if (from.pile === 'tableau') {
          const slot = playerState.tableau && playerState.tableau[from.index] ? playerState.tableau[from.index] : [];
          movingCard = slot[(typeof from.start === 'number') ? from.start : (slot.length - 1)];
        } else if (from.pile === 'nertzPile') {
          movingCard = playerState.nertzPile && playerState.nertzPile.slice(-1)[0];
        } else if (from.pile === 'discard') {
          movingCard = playerState.discard && playerState.discard.slice(-1)[0];
        }
      }
      const baseRect = el.getBoundingClientRect();
      // For tableau targets, show a card-sized highlight where the next card would sit
      if (opt.type === 'tableau') {
        const stackOffset = 16; // must match render stackOffset
        const cardW = 60;
        const cardH = 84;
        let slotLen = 0;
        if (playerState && Array.isArray(playerState.tableau) && playerState.tableau[opt.index]) {
          slotLen = playerState.tableau[opt.index].length;
        }
        const left = baseRect.left + Math.max(0, (baseRect.width - cardW) / 2);
        const top = baseRect.top + slotLen * stackOffset;
        const rect = { left, top, width: cardW, height: cardH };
        targets.push({ option: opt, rect, cardFilename: cardFilenameFromObj(movingCard) });
      } else {
        // foundation: highlight entire foundation pile area
        const rect = baseRect;
        targets.push({ option: opt, rect, cardFilename: cardFilenameFromObj(movingCard) });
      }
    });
    setOverlayTargets(targets);
  }, [actionMenu, gameState.players]);

  // Animate a flying card from startRect to endRect using the provided src image
  function animateLocalMove(startRect, endRect, src, meta) {
    return;
  }

  // Animate a single card but mark liveAnimations so source/target are hidden during the flight
  function animateSingleCard({ playerId, from, to, startRect, endRect, src }) {
    return;
  }

  // Animate a stack/sequence of cards from a tableau source to a tableau target (or single card)
  function animateSequence({ playerId, from, to, cards }) {
    return;
  }

  // Helper to compute destination rect for an option (similar to overlay logic)
  function computeTargetRectForOption(option, playerId) {
    try {
      if (!option) return null;
      if (option.type === 'foundation') {
        const el = foundationRefs.current[option.index];
        if (!el || !el.getBoundingClientRect) return null;
        return el.getBoundingClientRect();
      }
      if (option.type === 'tableau') {
        const el = tableauRefs.current[`${playerId}:${option.index}`];
        if (!el || !el.getBoundingClientRect) return null;
        const baseRect = el.getBoundingClientRect();
        const stackOffset = 16;
        // measure current slot length from gameState players
        const pl = gameState.players.find(pl => String(pl.socketId) === String(playerId));
        let slotLen = 0;
        if (pl && Array.isArray(pl.tableau) && pl.tableau[option.index]) slotLen = pl.tableau[option.index].length;
        const cardW = 60, cardH = 84;
        const left = baseRect.left + Math.max(0, (baseRect.width - cardW) / 2);
        const top = baseRect.top + slotLen * stackOffset;
        return { left, top, width: cardW, height: cardH };
      }
    } catch (e) { return null; }
    return null;
  }

  if (!gameState) return <div>Loading Nertz...</div>;
  const { foundations, winner } = gameState;

  // Compute start rect for a given 'from' descriptor
  function computeStartRectForFrom(from, playerId) {
    try {
      if (!from) return null;
      if (from.pile === 'tableau') {
        const el = tableauRefs.current[`${playerId}:${from.index}`];
        if (!el || !el.getBoundingClientRect) return null;
        const baseRect = el.getBoundingClientRect();
        const stackOffset = 16;
        const cardW = 60, cardH = 84;
        const top = baseRect.top + ((typeof from.start === 'number' ? from.start : ( (Array.isArray(gameState.players.find(p=>String(p.socketId)===String(playerId)).tableau[from.index]) ? gameState.players.find(p=>String(p.socketId)===String(playerId)).tableau[from.index].length -1 : 0))) * stackOffset);
        const left = baseRect.left + Math.max(0, (baseRect.width - cardW) / 2);
        return { left, top, width: cardW, height: cardH };
      }
      if (from.pile === 'nertzPile') {
        const el = pileRefs.current[`nertz:${playerId}`];
        if (!el || !el.getBoundingClientRect) return null;
        return el.getBoundingClientRect();
      }
      if (from.pile === 'discard') {
        const el = pileRefs.current[`discard:${playerId}`];
        if (!el || !el.getBoundingClientRect) return null;
        return el.getBoundingClientRect();
      }
      if (from.pile === 'stock') {
        const el = pileRefs.current[`stock:${playerId}`];
        if (!el || !el.getBoundingClientRect) return null;
        return el.getBoundingClientRect();
      }
    } catch (e) { return null; }
    return null;
  }

  function getCardFromSource(playerState, from) {
    if (!playerState || !from) return null;
    if (from.pile === 'tableau') {
      const slot = playerState.tableau?.[from.index] || [];
      return slot[typeof from.start === 'number' ? from.start : slot.length - 1] || null;
    }
    if (from.pile === 'nertzPile') return playerState.nertzPile?.[playerState.nertzPile.length - 1] || null;
    if (from.pile === 'discard') return playerState.discard?.[playerState.discard.length - 1] || null;
    return null;
  }

  function getSourceCards(playerState, from) {
    if (!playerState || !from) return [];
    if (from.pile === 'tableau') {
      const slot = playerState.tableau?.[from.index] || [];
      return slot.slice(typeof from.start === 'number' ? from.start : Math.max(0, slot.length - 1));
    }
    const card = getCardFromSource(playerState, from);
    return card ? [card] : [];
  }

  function buildPossibleTargets(playerState, from) {
    const options = computeDestinationsForCard(playerState, from);
    return options.map((option) => ({
      option,
      key: option.type === 'foundation' ? `foundation-${option.index}` : `tableau-${playerState.socketId}-${option.index}`,
    }));
  }

  function getTouchPoint(event) {
    return (event.touches && event.touches[0]) || (event.changedTouches && event.changedTouches[0]) || null;
  }

  function getDropTargetFromTouch(event) {
    const touch = getTouchPoint(event);
    if (!touch) return null;
    let node = document.elementFromPoint(touch.clientX, touch.clientY);
    while (node && node !== document.body) {
      if (node.dataset?.dropTarget) return node.dataset.dropTarget;
      node = node.parentElement;
    }
    return null;
  }

  function buildTouchDragPreview(event, playerState, from) {
    const touch = getTouchPoint(event);
    const cards = getSourceCards(playerState, from);
    if (!touch || !cards.length) return null;
    return { x: touch.clientX, y: touch.clientY, cards };
  }

  function buildDesktopDragPreview(cards) {
    if (typeof document === 'undefined' || !cards.length) return null;
    clearDesktopDragPreview();

    const preview = document.createElement('div');
    preview.style.position = 'fixed';
    preview.style.left = '-10000px';
    preview.style.top = '-10000px';
    preview.style.width = `${CARD_WIDTH}px`;
    preview.style.height = `${CARD_HEIGHT + Math.max(0, cards.length - 1) * TABLEAU_STACK_OFFSET}px`;
    preview.style.pointerEvents = 'none';
    preview.style.zIndex = '-1';

    cards.forEach((card, index) => {
      const img = document.createElement('img');
      const filename = getCardFilename(card);
      if (!filename) return;
      img.src = `/cards/${filename}`;
      img.alt = filename.replace('.png', '');
      img.draggable = false;
      img.style.position = 'absolute';
      img.style.left = '0';
      img.style.top = `${index * TABLEAU_STACK_OFFSET}px`;
      img.style.width = `${CARD_WIDTH}px`;
      img.style.height = `${CARD_HEIGHT}px`;
      img.style.borderRadius = '8px';
      img.style.boxShadow = '0 8px 16px rgba(15,23,42,0.14)';
      img.style.objectFit = 'cover';
      preview.appendChild(img);
    });

    document.body.appendChild(preview);
    desktopDragPreviewRef.current = preview;
    return preview;
  }

  function performMoveOption(playerState, from, option, startRect = null) {
    if (!playerState || !from || !option) return;
    const cardObj = getCardFromSource(playerState, from);
    if (!cardObj) return;

    try {
      const targetRect = computeTargetRectForOption(option, playerState.socketId);
      const src = `/cards/${getCardFilename(cardObj)}`;
      if (src && startRect && targetRect) {
        if (from.pile === 'tableau' && option.type === 'tableau') {
          const count = playerState.tableau[from.index].length - (typeof from.start === 'number' ? from.start : playerState.tableau[from.index].length - 1);
          if (count > 1) {
            const seqCards = playerState.tableau[from.index].slice(typeof from.start === 'number' ? from.start : playerState.tableau[from.index].length - 1);
            animateSequence({ playerId: playerState.socketId, from, to: { index: option.index }, cards: seqCards });
          } else {
            animateSingleCard({ playerId: playerState.socketId, from, to: { pile: 'tableau', index: option.index }, startRect, endRect: targetRect, src });
          }
        } else if (from.pile === 'tableau') {
          animateSingleCard({ playerId: playerState.socketId, from, to: { pile: 'foundation', index: option.index }, startRect, endRect: targetRect, src });
        } else {
          animateLocalMove(startRect, targetRect, src, { playerId: playerState.socketId, to: option.type === 'foundation' ? { pile: 'foundation', index: option.index } : { pile: 'tableau', index: option.index } });
        }
      }
    } catch (error) {
      // ignore animation errors and still submit the move
    }

    if (option.type === 'foundation') {
      submitAction && submitAction({ type: 'moveCard', from, to: { pile: 'foundation' }, card: cardObj }).catch(() => {});
    } else if (option.type === 'tableau') {
      const count = from.pile === 'tableau'
        ? playerState.tableau[from.index].length - (typeof from.start === 'number' ? from.start : playerState.tableau[from.index].length - 1)
        : 1;
      submitAction && submitAction({ type: 'moveCard', from, to: { pile: 'tableau', index: option.index }, card: cardObj, count }).catch(() => {});
    }
  }

  function handleDragStart(event, playerState, from) {
    const targets = buildPossibleTargets(playerState, from);
    if (!targets.length) return;
    const cards = getSourceCards(playerState, from);
    clearActionMenu();
    setSelectedSource({ playerId: playerState.socketId, from });
    setHighlightedTargets(targets.map((target) => target.key));
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', JSON.stringify({ playerId: playerState.socketId, from }));
      if (cards.length > 1) {
        const preview = buildDesktopDragPreview(cards);
        if (preview && event.dataTransfer.setDragImage) {
          event.dataTransfer.setDragImage(preview, Math.round(CARD_WIDTH / 2), Math.round(Math.min(CARD_HEIGHT / 2, 24)));
        }
      }
    }
  }

  function handleDragEnd() {
    clearSelectionState();
  }

  function handleTouchDragStart(event, playerState, from) {
    const targets = buildPossibleTargets(playerState, from);
    if (!targets.length) return;
    clearActionMenu();
    const source = { playerId: playerState.socketId, from };
    setSelectedSource(source);
    setHighlightedTargets(targets.map((target) => target.key));
    dragTouch.current = source;
    setTouchDragPreview(buildTouchDragPreview(event, playerState, from));
  }

  function handleTouchDragMove(event) {
    const touch = getTouchPoint(event);
    if (!touch) return;
    setTouchDragPreview((current) => {
      if (!current) return current;
      return { ...current, x: touch.clientX, y: touch.clientY };
    });
  }

  function handleTouchDragEnd(event) {
    if (!dragTouch.current) return;
    const source = dragTouch.current;
    const playerState = gameState.players.find((pl) => String(pl.socketId) === String(source.playerId));
    const targets = buildPossibleTargets(playerState, source.from);
    const dropKey = getDropTargetFromTouch(event);
    const match = targets.find((target) => target.key === dropKey);
    if (match) {
      const startRect = computeStartRectForFrom(source.from, source.playerId);
      performMoveOption(playerState, source.from, match.option, startRect);
    }
    clearSelectionState();
  }

  function handleFoundationDragOver(event, foundationIndex) {
    if (!selectedSource) return;
    if (!isHighlightedTarget(`foundation-${foundationIndex}`)) return;
    event.preventDefault();
  }

  function handleFoundationDropEvent(event, foundationIndex) {
    if (!selectedSource) return;
    if (!isHighlightedTarget(`foundation-${foundationIndex}`)) return;
    event.preventDefault();
    const playerState = gameState.players.find((pl) => String(pl.socketId) === String(selectedSource.playerId));
    const startRect = computeStartRectForFrom(selectedSource.from, selectedSource.playerId);
    performMoveOption(playerState, selectedSource.from, { type: 'foundation', index: foundationIndex }, startRect);
    clearSelectionState();
  }

  function handleTableauDragOver(event, playerId, tableauIndex) {
    if (!selectedSource) return;
    if (!isHighlightedTarget(`tableau-${playerId}-${tableauIndex}`)) return;
    event.preventDefault();
  }

  function handleTableauDropEvent(event, playerId, tableauIndex) {
    if (!selectedSource) return;
    if (!isHighlightedTarget(`tableau-${playerId}-${tableauIndex}`)) return;
    event.preventDefault();
    const playerState = gameState.players.find((pl) => String(pl.socketId) === String(selectedSource.playerId));
    const startRect = computeStartRectForFrom(selectedSource.from, selectedSource.playerId);
    performMoveOption(playerState, selectedSource.from, { type: 'tableau', index: tableauIndex }, startRect);
    clearSelectionState();
  }

  function clearActionMenu() {
    setActionMenu(null);
  setOverlayTargets([]);
  }

  // helper to check a sequence validity (used both for rendering and computing destinations)
  function isValidSequence(seq) {
    if (!seq || seq.length < 1) return false;
    const redSuits = ["hearts", "diamonds"];
    const order = [
      "A",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
      "J",
      "Q",
      "K",
    ];
    for (let k = 0; k < seq.length - 1; k++) {
      const c1 = seq[k], c2 = seq[k + 1];
      const color1 = redSuits.includes(c1.suit) ? "red" : "black";
      const color2 = redSuits.includes(c2.suit) ? "red" : "black";
      if (color1 === color2) return false;
      if (order.indexOf(c1.rank) !== order.indexOf(c2.rank) + 1) return false;
    }
    return true;
  }

  // Compute legal destinations for a clicked card (or sequence starting at startIndex)
  function computeDestinationsForCard(playerState, from) {
    // from: { pile: 'tableau'|'nertzPile'|'discard', index?, start? }
  let options = [];
    let card = null;
    let seq = [];
    if (from.pile === 'tableau') {
      const slot = (playerState.tableau && playerState.tableau[from.index]) ? playerState.tableau[from.index] : [];
      // seq starts at clicked index if provided, otherwise just the top card
      seq = (slot || []).slice(typeof from.start === 'number' ? from.start : Math.max(0, slot.length - 1));
      card = seq[0];
    } else if (from.pile === 'nertzPile') {
      card = playerState.nertzPile && playerState.nertzPile[playerState.nertzPile.length - 1];
      seq = card ? [card] : [];
    } else if (from.pile === 'discard') {
      card = playerState.discard && playerState.discard[playerState.discard.length - 1];
      seq = card ? [card] : [];
    }
    if (!card) return options;

    // debug: show sequence and card used to compute destinations
    try {
      const seqBrief = (seq || []).map(c => ({ rank: c.rank, suit: c.suit }));
      console.debug('computeDestinationsForCard', { from, card: { rank: card.rank, suit: card.suit }, seq: seqBrief });
    } catch (e) {
      console.debug('computeDestinationsForCard (error building debug)', e);
    }

  // Tableau destinations
    if (from.pile === "tableau") {
      // move a sequence starting at start (seq already computed above)
      if (isValidSequence(seq)) {
        for (let j = 0; j < playerState.tableau.length; j++) {
          if (j === from.index) continue;
          const slot = playerState.tableau[j] || [];
          if (slot.length === 0) {
            options.push({ type: "tableau", index: j });
          } else {
            const top = slot[slot.length - 1];
            const redSuits = ["hearts", "diamonds"];
            const topColor = redSuits.includes(top.suit) ? "red" : "black";
            const seqColor = redSuits.includes(seq[0].suit) ? "red" : "black";
            const order = ["A","2","3","4","5","6","7","8","9","10","J","Q","K"];
            const rankOk = order.indexOf(seq[0].rank) === order.indexOf(top.rank) - 1;
            const colorOk = seqColor !== topColor;
            console.debug('checkTableauTarget', { col: j, top: { rank: top.rank, suit: top.suit }, seq0: { rank: seq[0].rank, suit: seq[0].suit }, rankOk, colorOk });
            if (rankOk && colorOk) {
              options.push({ type: "tableau", index: j });
            }
          }
        }
      }
      // Foundation play: only allow playing to foundation if the sequence is a single card (top card)
      if (seq.length === 1) {
        // push any foundation index that accepts this card
        const order = ["A","2","3","4","5","6","7","8","9","10","J","Q","K"];
        for (let fi = 0; fi < foundations.length; fi++) {
          const fp = foundations[fi];
          if (!fp || fp.length === 0) {
            if (card.rank === 'A') { options.push({ type: 'foundation', index: fi }); break; }
          } else {
            const top = fp[fp.length - 1];
            if (top.suit === card.suit && order.indexOf(card.rank) === order.indexOf(top.rank) + 1) { options.push({ type: 'foundation', index: fi }); break; }
          }
        }
      }
    } else {
      // from nertzPile or discard: single card moves to tableau if legal
  // Foundation play for non-tableau piles
      // push any foundation index that accepts this card
      const order = ["A","2","3","4","5","6","7","8","9","10","J","Q","K"];
      for (let fi = 0; fi < foundations.length; fi++) {
        const fp = foundations[fi];
        if (!fp || fp.length === 0) {
          if (card.rank === 'A') { options.push({ type: 'foundation', index: fi }); break; }
        } else {
          const top = fp[fp.length - 1];
          if (top.suit === card.suit && order.indexOf(card.rank) === order.indexOf(top.rank) + 1) { options.push({ type: 'foundation', index: fi }); break; }
        }
      }
      for (let j = 0; j < playerState.tableau.length; j++) {
        const slot = playerState.tableau[j] || [];
        if (slot.length === 0) {
          // allow moving single card to empty slot if king (common rule) - reuse canMoveToTableau
          if (canMoveToTableau(card, slot)) options.push({ type: "tableau", index: j });
        } else {
          if (canMoveToTableau(card, slot)) options.push({ type: "tableau", index: j });
        }
      }
    }

    // If there are multiple foundation targets that accept this card, only offer the first
    try {
      const foundationOpts = options.filter((o) => o.type === 'foundation');
      if (foundationOpts.length > 1) {
        const firstIdx = Math.min(...foundationOpts.map((o) => o.index));
        options = options.filter((o) => !(o.type === 'foundation' && o.index !== firstIdx));
      }
    } catch (e) {
      // ignore any unexpected shape
    }

    return options;
  }

  // Handle a user clicking a rendered card
  function handleCardClick(e, playerState, from) {
    // prevent parent click from clearing the menu immediately
    if (e && e.stopPropagation) e.stopPropagation();
  console.debug("handleCardClick", { from, playerId: playerState.socketId });
    const opts = computeDestinationsForCard(playerState, from);
  console.debug("computed options", opts);
  const id = `${playerState.socketId}:${from.pile}:${from.index || ''}:${typeof from.start === 'number' ? from.start : ''}`;
    if (!opts || opts.length === 0) {
      // no legal moves - show a small inline message so clicks give feedback
      setActionMenu({ id, playerId: playerState.socketId, from, options: [], empty: true });
      return;
    }
    if (opts.length === 1) {
      const startEl = e && e.currentTarget ? e.currentTarget : (e && e.target ? e.target : null);
      const startRect = startEl && startEl.getBoundingClientRect ? startEl.getBoundingClientRect() : null;
      performMoveOption(playerState, from, opts[0], startRect);
      setActionMenu(null);
      return;
    }

  // multiple options: show small inline menu attached to this card
  setActionMenu({ id, playerId: playerState.socketId, from, options: opts });
  }

  // Similar to handleCardClick but allow the event to bubble when there are no legal moves
  // so the parent's onClick (e.g. flipStock) can run. If we handle an action (single or multiple
  // options), we stop propagation to avoid triggering the parent's handler.
  function handleCardClickMaybeBubble(e, playerState, from) {
    const opts = computeDestinationsForCard(playerState, from);
  console.debug("handleCardClickMaybeBubble", { from, playerId: playerState.socketId, opts });
  const id = `${playerState.socketId}:${from.pile}:${from.index || ''}:${typeof from.start === 'number' ? from.start : ''}`;
    if (!opts || opts.length === 0) {
      // no legal moves - show a small inline message so clicks give feedback
      setActionMenu({ id, playerId: playerState.socketId, from, options: [], empty: true });
      // allow event to bubble so parent can handle flipStock
      return;
    }
      if (opts.length === 1) {
      // perform the only action immediately and stop propagation
      if (e && e.stopPropagation) e.stopPropagation();
      const opt = opts[0];
      if (opt.type === "foundation") {
  submitAction && submitAction({ type: "moveCard", from, to: { pile: "foundation" }, card: (from.pile === 'tableau' ? playerState.tableau[from.index][(typeof from.start === 'number'? from.start: playerState.tableau[from.index].length-1)] : (from.pile === 'nertzPile' ? playerState.nertzPile[playerState.nertzPile.length-1] : playerState.discard[playerState.discard.length-1])) }).catch(()=>{});
      } else if (opt.type === "tableau") {
  submitAction && submitAction({ type: "moveCard", from, to: { pile: "tableau", index: opt.index }, card: (from.pile === 'tableau' ? playerState.tableau[from.index][(typeof from.start === 'number' ? from.start : (playerState.tableau[from.index].length-1))] : (from.pile === 'nertzPile' ? playerState.nertzPile[playerState.nertzPile.length-1] : playerState.discard[playerState.discard.length-1])), count: from.pile === 'tableau' ? (playerState.tableau[from.index].length - (typeof from.start === 'number' ? from.start : 0)) : 1 }).catch(()=>{});
      }
      setActionMenu(null);
      return;
    }
    // multiple options: stop propagation and show inline menu
    if (e && e.stopPropagation) e.stopPropagation();
    setActionMenu({ id, playerId: playerState.socketId, from, options: opts });
  }

  function handleOptionClick(option) {
    if (!actionMenu) return;
    const from = actionMenu.from;
    // clear overlays since we are performing the option
    setOverlayTargets([]);
    const playerState = gameState.players.find(pl => String(pl.socketId)===String(actionMenu.playerId));
    performMoveOption(playerState, from, option, computeStartRectForFrom(from, actionMenu.playerId));
    setActionMenu(null);
  }

  // Handle flipping stock: animate top stock card to discard quickly then dispatch the flipStock action
  function handleFlipStock(player) {
    submitAction && submitAction({ type: 'flipStock' }).catch(()=>{});
  }

  return (
  <div className="nertz-game" style={{ padding: 16, position: 'relative', borderRadius: 24, background: isDarkMode ? 'linear-gradient(135deg, rgba(2,6,23,0.98), rgba(15,23,42,0.98) 45%, rgba(6,78,59,0.82))' : 'linear-gradient(135deg, rgba(236,253,245,0.92), rgba(239,246,255,0.96))', boxShadow: isDarkMode ? '0 18px 42px rgba(2,6,23,0.45)' : '0 16px 40px rgba(15,23,42,0.12)', color: isDarkMode ? 'rgb(226,232,240)' : 'inherit', border: isDarkMode ? '1px solid rgba(30,41,59,0.9)' : 'none' }}>
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      {/* Animated flying cards */}
      {animatedCards.map(a => (
        <img key={a.id} src={a.src} alt="anim" style={{ position: 'fixed', left: a.left, top: a.top, width: a.width, height: a.height, transition: a.transition, pointerEvents: 'none', zIndex: 12000 }} draggable={false} />
      ))}
      {touchDragPreview ? (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            transform: `translate(${touchDragPreview.x - CARD_WIDTH / 2}px, ${touchDragPreview.y - CARD_HEIGHT / 2}px)`,
            pointerEvents: 'none',
            zIndex: 12001,
          }}
        >
          <div
            style={{
              width: CARD_WIDTH,
              height: CARD_HEIGHT + Math.max(0, touchDragPreview.cards.length - 1) * TABLEAU_STACK_OFFSET,
              position: 'relative',
            }}
          >
            {touchDragPreview.cards.map((card, index) => (
              <div
                key={`${card.code || card.filename || `${card.rank}-${card.suit}`}-${index}`}
                style={{ position: 'absolute', top: index * TABLEAU_STACK_OFFSET, left: 0, width: CARD_WIDTH, height: CARD_HEIGHT, opacity: 0.96 }}
              >
                <CardImg card={card} style={{ width: '100%', height: '100%', marginRight: 0 }} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div
        className="foundations-container"
        style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}
      >
        {/* PILE: foundations (shared foundation piles for all players) */}
        <div
          className="foundation-wide-box"
          style={{
            background: isDarkMode ? 'linear-gradient(180deg, rgba(15,23,42,0.96), rgba(17,24,39,0.94))' : "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(241,245,249,0.96))",
            borderRadius: 18,
            padding: 10,
            minWidth: 280,
            display: "grid",
            gridTemplateColumns: "repeat(8, auto)",
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
            
            
          }}
        >
          {foundations.map((pile, i) => {
            const animatingFoundation = liveAnimations.some(a => a.to && a.to.pile === 'foundation' && a.to.index === i);
            return (
              <div
                key={i}
                className="foundation-pile"
                ref={(el) => (foundationRefs.current[i] = el)}
                onDragOver={(event) => handleFoundationDragOver(event, i)}
                onDrop={(event) => handleFoundationDropEvent(event, i)}
                data-drop-target={`foundation-${i}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: 36,
                  borderRadius: 10,
                  boxShadow: isHighlightedTarget(`foundation-${i}`) ? '0 0 0 3px rgba(125,211,252,0.28)' : 'none',
                  background: isHighlightedTarget(`foundation-${i}`) ? 'rgba(186,230,253,0.18)' : 'transparent',
                }}
              >
                {((pile.length === 0) || animatingFoundation) ? (
                  <EmptySlot foundation highlighted={isHighlightedTarget(`foundation-${i}`)} title="Empty foundation" style={{ width: 32, height: 48, borderRadius: 8 }} />
                ) : (
                  (() => {
                    const top = pile[pile.length - 1] || {};
                    const rank = top.rank || top.value || "";
                    const suit = top.suit || "";
                    const suitSymbols = { hearts: "♥", diamonds: "♦", clubs: "♣", spades: "♠" };
                    const isRed = suit === "hearts" || suit === "diamonds";
                    return (
                      <div style={{ width: 32, height: 48, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? 'rgba(248,250,252,0.96)' : '#fff', border: isDarkMode ? '1px solid rgba(148,163,184,0.28)' : '1px solid rgba(0,0,0,0.06)' }}>
                        <div style={{ textAlign: 'center', lineHeight: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: isRed ? '#b91c1c' : '#111' }}>{String(rank)}</div>
                          <div style={{ fontSize: 20, color: isRed ? '#b91c1c' : '#111' }}>{suitSymbols[suit] || ''}</div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Overlay click zones positioned above possible destinations */}
      {overlayTargets.map((t, i) => {
        const r = t.rect;
        if (!r) return null;
        // pulse when user has multiple choices
        const hasMultiple = actionMenu && actionMenu.options && actionMenu.options.length > 1;
        const style = {
          position: 'fixed',
          left: r.left + 'px',
          top: r.top + 'px',
          width: r.width + 'px',
          height: r.height + 'px',
            background: 'rgba(252,211,77,0.28)', // yellow-300 with alpha
            border: '2px solid rgba(250,204,21,0.9)', // yellow-400
          borderRadius: 6,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          };
        // add pulsate animation styles when multiple choices exist
        if (hasMultiple) {
          style.animation = 'nertz-pulse 1000ms ease-in-out infinite';
        }
        return (
          <div key={i} style={style} onClick={() => handleOptionClick(t.option)} title={t.option.type === 'foundation' ? 'Play to foundation' : `Move to tableau ${t.option.index+1}`} />
        );
      })}

      {/* Pulsate keyframes (inlined) */}
      <style>
        {`@keyframes nertz-pulse { 0% { box-shadow: 0 0 0 0 rgba(250,204,21,0.6); } 70% { box-shadow: 0 0 0 14px rgba(250,204,21,0.0); } 100% { box-shadow: 0 0 0 0 rgba(250,204,21,0.0); } }`}
      </style>
      <div
        className="players"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "center",
        }}
      >
        {sortedPlayers.map((p) => {
          const isCurrentUser =
            currentPlayer && String(p.socketId) === String(currentPlayer.socketId);
          // compute tableau visual height so UI elements below (action menu, score)
          // are pushed down as the tableau gets taller
          const tableauCols = Array.isArray(p.tableau) ? p.tableau : [];
          const maxColLen = tableauCols.length > 0 ? Math.max(...tableauCols.map((c) => (Array.isArray(c) ? c.length : 0))) : 0;
          const stackOffset = TABLEAU_STACK_OFFSET; // vertical offset between stacked cards
          const cardHeight = CARD_HEIGHT; // matches CardImg height
          const tableauMinHeight = Math.max(110, maxColLen * stackOffset + cardHeight);
          return (
            <div
              key={p.socketId}
              className={`player-area${isCurrentUser ? " your-area" : ""}`}
              style={{
                background: isDarkMode
                  ? (isCurrentUser
                      ? 'linear-gradient(180deg, rgba(6,78,59,0.42), rgba(15,23,42,0.96))'
                      : 'linear-gradient(180deg, rgba(30,41,59,0.9), rgba(15,23,42,0.96))')
                  : (isCurrentUser
                      ? "linear-gradient(180deg, rgba(240,253,244,0.96), rgba(255,255,255,0.98))"
                      : "linear-gradient(180deg, rgba(248,250,252,0.96), rgba(255,255,255,0.98))"),
                border: isDarkMode ? '1px solid rgba(51,65,85,0.9)' : "1px solid rgba(148,163,184,0.28)",
                borderRadius: 20,
                padding: 18,
                minWidth: 320,
                boxShadow: isDarkMode
                  ? (isCurrentUser ? '0 16px 32px rgba(2,6,23,0.34)' : '0 16px 32px rgba(2,6,23,0.28)')
                  : (isCurrentUser ? '0 14px 28px rgba(21,128,61,0.12)' : '0 12px 24px rgba(15,23,42,0.06)'),
              }}
            >
              <h4 style={{ marginBottom: 8, color: isDarkMode ? 'rgb(226,232,240)' : 'inherit' }}>
                {p.username} {isCurrentUser ? "(You)" : ""} - Score : {typeof (gameState.scores && gameState.scores[p.socketId]) === 'number' ? gameState.scores[p.socketId] : 0}
              </h4>
              <div
                onClick={() => clearActionMenu()}
                style={{
                  marginBottom: 6,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                }}
              >
                {/* PILE: nertzPile (player's personal Nertz pile) */}
                <div
                  style={{
                    position: "relative",
                    width: CARD_WIDTH,
                    height: CARD_HEIGHT,
                    display: "inline-block",
                  }}
                  ref={(el) => (pileRefs.current[`nertz:${p.socketId}`] = el)}
                >
                  {Array.isArray(p.nertzPile) && p.nertzPile.length > 0 ? (
                    <>
                      <CardButton
                        card={p.nertzPile[p.nertzPile.length - 1]}
                        onClick={(e) => handleCardClick(e, p, { pile: 'nertzPile' })}
                        onDragStart={isCurrentUser ? (event) => handleDragStart(event, p, { pile: 'nertzPile' }) : undefined}
                        onDragEnd={isCurrentUser ? handleDragEnd : undefined}
                        draggable={isCurrentUser}
                        onTouchDragStart={isCurrentUser ? (event) => handleTouchDragStart(event, p, { pile: 'nertzPile' }) : undefined}
                        onTouchDragMove={isCurrentUser ? handleTouchDragMove : undefined}
                        onTouchDragEnd={isCurrentUser ? handleTouchDragEnd : undefined}
                        selected={selectedSource && selectedSource.playerId === p.socketId && selectedSource.from.pile === 'nertzPile'}
                        title="Click or drag this card"
                      />
                      {/* Show pile size in a pill at upper right */}
                      <span
                        style={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          background: "#7b1fa2",
                          color: "#fff",
                          borderRadius: 12,
                          fontSize: 12,
                          padding: "2px 8px",
                          minWidth: 24,
                          textAlign: "center",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                          zIndex: 10,
                        }}
                      >
                        {p.nertzPile.length}
                      </span>
                      {/* Play to foundation button if legal */}
                      {/* Play button removed: use card click or inline action menu to play to foundation */}
                    </>
                  ) : (
                    <EmptySlot onClick={(e) => handleCardClick(e, p, { pile: 'nertzPile' })} title="Nertz pile (empty)" />
                  )}
                </div>
                {/* Stock and Discard (right, close together, aligned at top) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    marginLeft: 16,
                  }}
                >
                  {/* PILE: stock (player's draw/stock pile) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      position: "relative",
                      minWidth: 60,
                      height: 84,
                      cursor:
                        isCurrentUser &&
                        ((Array.isArray(p.stock) && p.stock.length > 0) ||
                          (Array.isArray(p.discard) && p.discard.length > 0))
                          ? "pointer"
                          : "default",
                    }}
                    onClick={
                        isCurrentUser &&
                        ((Array.isArray(p.stock) && p.stock.length > 0) ||
                          (Array.isArray(p.discard) && p.discard.length > 0))
                          ? () => handleFlipStock(p)
                          : undefined
                    }
                  >
                    <div
                      style={{ position: "relative", width: CARD_WIDTH, height: CARD_HEIGHT }}
                      ref={(el) => (pileRefs.current[`stock:${p.socketId}`] = el)}
                    >
                      {Array.isArray(p.stock) && p.stock.length > 0 ? (
                        <>
                          {/* Only show a single card back, with pill for count */}
                                <div onClick={(e) => handleCardClickMaybeBubble(e, p, { pile: 'stock' })} style={{ cursor: 'pointer' }} title="Click to act on stock">
                                  <CardImg
                              card="card_back.png"
                              style={{
                                position: "absolute",
                                left: 0,
                                zIndex: 1,
                                opacity: 0.7,
                              }}
                            />
                          </div>
                          <span
                            style={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              background: "#1976d2",
                              color: "#fff",
                              borderRadius: 12,
                              fontSize: 12,
                              padding: "2px 8px",
                              minWidth: 20,
                              textAlign: "center",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                              zIndex: 10,
                            }}
                          >
                            {p.stock.length}
                          </span>
                        </>
                      ) : (
                        <EmptySlot
                          darkMode={isDarkMode}
                          onClick={(e) => handleCardClickMaybeBubble(e, p, { pile: 'stock' })}
                          title="Recycle discard into stock"
                          content={
                            <div
                              style={{
                                color: isDarkMode ? 'rgba(148,163,184,0.9)' : 'rgba(71,85,105,0.72)',
                                fontSize: 24,
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              ↺
                            </div>
                          }
                        />
                      )}
                    </div>
                  </div>
                  {/* PILE: discard (player's discard/waste pile, right of stock) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      position: "relative",
                      minWidth: 60,
                      height: 84,
                      marginLeft: 8,
                    }}
                  >
                    <div
                      style={{ position: "relative", width: CARD_WIDTH, height: CARD_HEIGHT }}
                      ref={(el) => (pileRefs.current[`discard:${p.socketId}`] = el)}
                    >
                      {Array.isArray(p.discard) && p.discard.length > 0 ? (
                        (() => {
                          // if a discard animation to this player's discard is in flight, show placeholder until animation completes
                          const animToDiscard = liveAnimations.some(a => a.to && a.to.pile === 'discard' && String(a.playerId) === String(p.socketId));
                          if (animToDiscard) {
                            return (
                              <EmptySlot onClick={(e) => handleCardClick(e, p, { pile: 'discard' })} title="Click to act on discard" />
                            );
                          }
                          // Show up to 3 discard cards stacked, with the topmost (last) being playable.
                          const displayCount = Math.min(3, p.discard.length);
                          const slice = p.discard.slice(-displayCount);
                          const stackOffsetX = 12; // horizontal offset so left edges of underlying cards are visible
                          return (
                            <div style={{ position: 'relative', width: CARD_WIDTH + (displayCount - 1) * stackOffsetX, height: CARD_HEIGHT }}>
                              {slice.map((cardObj, idx) => {
                                const left = idx * stackOffsetX;
                                const isTop = idx === slice.length - 1;
                                const key = (cardObj && (cardObj.code || cardObj.filename)) || `${cardObj && cardObj.rank ? cardObj.rank : 'x'}:${cardObj && cardObj.suit ? cardObj.suit : 's'}:${idx}`;
                                return (
                                  <span key={key} style={{ position: 'absolute', left: left, top: 0, zIndex: 2 + idx }}>
                                    {isTop ? (
                                      <CardButton
                                        card={cardObj}
                                        onClick={(e) => handleCardClick(e, p, { pile: 'discard' })}
                                        onDragStart={isCurrentUser ? (event) => handleDragStart(event, p, { pile: 'discard' }) : undefined}
                                        onDragEnd={isCurrentUser ? handleDragEnd : undefined}
                                        draggable={isCurrentUser}
                                        onTouchDragStart={isCurrentUser ? (event) => handleTouchDragStart(event, p, { pile: 'discard' }) : undefined}
                                        onTouchDragMove={isCurrentUser ? handleTouchDragMove : undefined}
                                        onTouchDragEnd={isCurrentUser ? handleTouchDragEnd : undefined}
                                        selected={selectedSource && selectedSource.playerId === p.socketId && selectedSource.from.pile === 'discard'}
                                        borderless
                                        title="Click or drag this discard card"
                                      />
                                    ) : (
                                      <CardImg card={cardObj} />
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          );
                        })()
                      ) : (
                        <EmptySlot onClick={(e) => handleCardClick(e, p, { pile: 'discard' })} title="Click to act on discard" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* PILE: tableau (player tableau columns where sequences are built) */}
              <div style={{ marginBottom: 6, minHeight: tableauMinHeight }}>
                <b>Tableau:</b>
                <div style={{ display: "flex", gap: 8 }}>
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          minHeight: 110,
                          minWidth: CARD_WIDTH,
                          borderRadius: 12,
                          boxShadow: isHighlightedTarget(`tableau-${p.socketId}-${i}`) ? '0 0 0 3px rgba(125,211,252,0.28)' : 'none',
                          background: isHighlightedTarget(`tableau-${p.socketId}-${i}`) ? 'rgba(186,230,253,0.16)' : 'transparent',
                        }}
                        ref={(el) => (tableauRefs.current[`${p.socketId}:${i}`] = el)}
                        onDragOver={(event) => handleTableauDragOver(event, p.socketId, i)}
                        onDrop={(event) => handleTableauDropEvent(event, p.socketId, i)}
                        data-drop-target={`tableau-${p.socketId}-${i}`}
                      >
                        {(() => {
                          // Always render the base empty placeholder at the bottom of the pile.
                          // This placeholder remains visible (under the cards) so when the column is moved away
                          // the + indicator remains.
                          const placeholder = (
                            <EmptySlot
                              darkMode={isDarkMode}
                              highlighted={isHighlightedTarget(`tableau-${p.socketId}-${i}`)}
                              onClick={(e) => handleCardClick(e, p, { pile: 'tableau', index: i })}
                              onDragOver={(event) => handleTableauDragOver(event, p.socketId, i)}
                              onDrop={(event) => handleTableauDropEvent(event, p.socketId, i)}
                              dataDropTarget={`tableau-${p.socketId}-${i}`}
                              title="Empty tableau - click or drop here"
                              style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
                            />
                          );

                          // If target column is currently animating into, do not reveal cards until animation completes
                          // Treat this as a "target animating in" (hide whole column) only
                          // when the incoming animation moves multiple cards (count > 1).
                          // Single-card moves (including tableau->tableau single card) should
                          // keep existing target cards visible; we'll instead suppress only the
                          // incoming card(s) until animation completes.
                          const isAnimatingTarget = liveAnimations.some(a => {
                            if (!(a && a.to && typeof a.to.index !== 'undefined')) return false;
                            if (!a.playerId || String(a.playerId) !== String(p.socketId)) return false;
                            return (a.count && Number(a.count) > 1);
                          });
                          if (isAnimatingTarget) {
                            return placeholder;
                          }

                          // If some cards in this column are being animated away, hide those cards here
                          const animSource = liveAnimations.find(a => a.from && typeof a.from.index !== 'undefined' && a.from.index === i && String(a.playerId) === String(p.socketId));
                          const startIdx = animSource && typeof animSource.from.start === 'number' ? animSource.from.start : null;

                          // If there is an incoming animation targeting this column, detect it
                          const animTo = liveAnimations.find(a => a.to && typeof a.to.index !== 'undefined' && a.to.index === i && String(a.playerId) === String(p.socketId));

                          if (Array.isArray(p.tableau) && p.tableau[i] && p.tableau[i].length > 0) {
                            let visibleItems = [];
                            // Priority: if source is animating away, respect that (hide moved-away cards)
                            if (startIdx !== null) {
                              visibleItems = p.tableau[i].map((c, idx) => ({ c, idx })).filter(x => x.idx < startIdx);
                            } else if (animTo && !(animTo.count && Number(animTo.count) > 1)) {
                              // single-card incoming move (from any source): keep existing cards visible
                              // but hide the incoming card if it has already been appended to the tableau in gameState.
                              const hideCount = (animTo.count && Number(animTo.count) > 0) ? Number(animTo.count) : 1;
                              const keepCount = Math.max(0, p.tableau[i].length - hideCount);
                              visibleItems = p.tableau[i].slice(0, keepCount).map((c, idx) => ({ c, idx }));
                            } else {
                              // default: show all cards
                              visibleItems = p.tableau[i].map((c, idx) => ({ c, idx }));
                            }

                            return (
                              <span style={{ position: 'relative', display: 'block' }}>
                                {placeholder}
                                {visibleItems.map(({ c: card, idx }) => {
                                  const cardKey = (card && (card.code || card.filename)) || `${card && card.rank ? card.rank : 'x'}:${card && card.suit ? card.suit : 's'}:${idx}`;
                                  return (
                                    <span key={cardKey} style={{ position: 'absolute', top: idx * TABLEAU_STACK_OFFSET, left: 0, zIndex: idx + 2 }}>
                                      <CardButton
                                        card={card}
                                        onClick={(e) => handleCardClick(e, p, { pile: 'tableau', index: i, start: idx })}
                                        onDragStart={isCurrentUser ? (event) => handleDragStart(event, p, { pile: 'tableau', index: i, start: idx }) : undefined}
                                        onDragEnd={isCurrentUser ? handleDragEnd : undefined}
                                        draggable={isCurrentUser}
                                        onTouchDragStart={isCurrentUser ? (event) => handleTouchDragStart(event, p, { pile: 'tableau', index: i, start: idx }) : undefined}
                                        onTouchDragMove={isCurrentUser ? handleTouchDragMove : undefined}
                                        onTouchDragEnd={isCurrentUser ? handleTouchDragEnd : undefined}
                                        selected={selectedSource && selectedSource.playerId === p.socketId && selectedSource.from.pile === 'tableau' && selectedSource.from.index === i && selectedSource.from.start === idx}
                                        title="Click or drag this card sequence"
                                      />
                                    </span>
                                  );
                                })}
                              </span>
                            );
                          }

                          // empty slot with placeholder underneath
                          return placeholder;
                        })()}
                        {/* Move from Nertz pile to tableau slot if legal */}
                        {/* nertz->tableau corner button removed: use card click or inline menu */}
                        {/* Move from Discard pile to tableau slot if legal */}
                        {/* discard->tableau corner button removed: use card click or inline menu */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              
            </div>
          );
        })}
      </div>
      {winner && (
        <div
          className="winner"
          style={{
            textAlign: "center",
            marginTop: 24,
            fontSize: 20,
            color: "#1976d2",
          }}
        >
          Winner: {winner}
        </div>
      )}
      {/* Add controls for actions here, e.g. play card, move tableau, flip stock */}
    </div>
  );
}

NertzGame.Guide = function NertzGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Nertz</h3>
      <ul className="list-disc ml-6">
        <li>
          Move cards to foundations (4 shared slots for each player playing) and tableau (4 columns per player) according to solitaire rules.
        </li>
        <li>First to empty their Nertz pile wins (The pile with the number in purple on top).</li>
        <li>Click on cards to see your options (or click the card directly to play if only one option is available).</li>
        <li>Click your stock pile (top left) to flip cards to your discard pile.</li>
        <li>Cards in the discard pile can be played to tableau or foundations.</li> 
        <li>Cards in the tableau can be moved as sequences or individually to other tableau columns or to foundations.</li>
        <li>Cards in the Nertz pile can only be played one at a time to tableau or foundations.</li>
        <li>You can only move sequences in the tableau if they are in descending order and alternating colors.</li>   
        <li>Foundations are built up by suit from Ace to King.</li>
        <li>Tableau columns are built down by alternating colors.</li>
        <li>If your stock pile is empty, you can flip your discard pile to replenish it.</li>
        <li>The game ends when a player empties their Nertz pile; scores are then calculated based on cards played to foundations and remaining cards in the Nertz pile.</li>
        <li>Have fun and play quickly!</li>
      </ul>
    </div>
  );
};

export default NertzGame;
